import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { Menu, X, ArrowUpRight } from "lucide-react";
import logo from "@/assets/logo.png";
import MagneticButton from "@/components/common/MagneticButton";

const links = [
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Process", href: "#process" },
  { label: "Case Studies", href: "#case-studies" },
  { label: "About", href: "#founder" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const menuItemsRef = useRef([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    const menu = menuRef.current;
    const items = menuItemsRef.current.filter(Boolean);

    if (open) {
      gsap.set(menu, { display: "block", opacity: 0, y: -12 });
      gsap.to(menu, {
        opacity: 1,
        y: 0,
        duration: 0.35,
        ease: "power3.out",
      });
      gsap.fromTo(
        items,
        { opacity: 0, y: 16 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.05,
          ease: "power3.out",
          delay: 0.1,
        }
      );
    } else {
      gsap.to(menu, {
        opacity: 0,
        y: -12,
        duration: 0.25,
        ease: "power2.in",
        onComplete: () => gsap.set(menu, { display: "none" }),
      });
    }
  }, [open]);

  useEffect(() => {
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current,
        { y: -40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, ease: "power3.out", delay: 0.2 }
      );
    }
  }, []);

  return (
    <header
      ref={headerRef}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div className="container-x">
        <div
          className={`mt-4 flex items-center justify-between rounded-full px-5 py-2.5 transition-all duration-500 ${
            scrolled ? "glass-strong shadow-lg shadow-orange-900/5" : "glass"
          }`}
        >
          <a href="#top" data-testid="logo-link" className="group flex items-center gap-2.5">
            <img src={logo} alt="DIBRAL" className="h-8 w-8 rounded-md" />
            <span className="text-[var(--body-fg)] font-semibold tracking-tight text-lg">DIBRAL</span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative px-3.5 py-1.5 text-sm text-[var(--muted-text)] hover:text-[var(--brand)] transition-colors rounded-full"
              >
                {l.label}
                <span className="pointer-events-none absolute left-3.5 right-3.5 -bottom-0 h-px scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100" style={{ background: "var(--brand)" }} />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <MagneticButton
              as="a"
              href="#booking"
              data-testid="navbar-book-btn"
              className="btn-primary hidden sm:inline-flex !px-4 !py-2 text-sm"
              strength={0.25}
              scale={1.03}
            >
              Book Consultation <ArrowUpRight className="w-4 h-4" />
            </MagneticButton>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--glass-border)] text-[var(--body-fg)] hover:bg-orange-500/5 transition"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div
        ref={menuRef}
        className="lg:hidden hidden mt-2 mx-4"
        data-testid="mobile-menu"
        style={{ display: "none" }}
      >
        <div className="glass-strong rounded-3xl p-4 space-y-1 shadow-xl shadow-orange-900/5">
          {links.map((l, i) => (
            <a
              key={l.href}
              ref={(el) => (menuItemsRef.current[i] = el)}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block px-4 py-3 text-[var(--body-fg)]/80 hover:bg-orange-500/5 rounded-2xl transition"
            >
              {l.label}
            </a>
          ))}
          <a
            ref={(el) => (menuItemsRef.current[links.length] = el)}
            href="#booking"
            onClick={() => setOpen(false)}
            className="btn-primary block text-center mt-2"
          >
            Book Free Consultation
          </a>
        </div>
      </div>
    </header>
  );
}
